import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { AsyncValidator, ControlValueAccessor, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { AlertService } from '@nano/common/alert';
import { Institution, InstitutionService } from '@nano/common/finance';
import { FormService } from '@nano/common/form';
import { indicateLoading } from '@nano/common/util';
import { ApiErrorCode, httpErrorToApiError } from '@nano/core/api';
import { ErrorService } from '@nano/core/error';
import { iif, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'app-account-number-input',
  templateUrl: './account-number-input.component.html',
  styles: ['input { text-transform: uppercase; }', 'input::placeholder { text-transform: none; }'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: AccountNumberInputComponent, multi: true },
    { provide: NG_ASYNC_VALIDATORS, useExisting: AccountNumberInputComponent, multi: true }
  ],
  standalone: false
})
export class AccountNumberInputComponent implements ControlValueAccessor, AsyncValidator {
  @Output() userMustProvideInstitution: EventEmitter<boolean> = new EventEmitter<boolean>(true);
  @Output() validating: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() institution: EventEmitter<Institution> = new EventEmitter<Institution>();

  @Input() autocompleted = false;
  @Input() nzSize: 'small' | 'default' | 'large' = 'default';
  @Input() placeholder: string;
  @Input() minlenght = 7;
  @Input() maxlength = 34;

  touched = false;
  disabled = false;

  value: string;

  private valueSubject: Subject<{ val: string; isInitial?: boolean }> = new ReplaySubject(1);
  readonly value$: Observable<string> = this.valueSubject.asObservable().pipe(map((value) => value.val));

  constructor(
    private elementRef: ElementRef,
    private formService: FormService,
    private errorService: ErrorService,
    private institutionService: InstitutionService,
    private alertService: AlertService
  ) {}

  @HostListener('input', ['$event']) onInput(event: InputEvent): void {
    const val = (event.target as HTMLInputElement).value;
    this.onChange(val);
    this.onTouched();
    this.valueSubject.next({ val });
  }

  validate(): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return this.valueSubject.asObservable().pipe(
      debounceTime(500),
      take(1),
      switchMap((value) =>
        iif(
          () => (value.isInitial && !this.autocompleted) || !value.val,
          of(null),
          this.institutionService.getInstitutions(value.val).pipe(
            indicateLoading(this.validating),
            map((response) => {
              const institutions = response.results;
              this.alertService.alert$.pipe(take(1)).subscribe((currentAlert) => {
                const institutionBlockedCode = String(ApiErrorCode.paymentToInstitutionIsNotAllowed);
                if (!currentAlert?.agreementLabelKey && currentAlert?.code !== institutionBlockedCode) {
                  this.alertService.cleanUp();
                }
              });
              if (institutions.length > 0) {
                this.formService.cleanFormItemAndExplain(this.elementRef);
                this.userMustProvideInstitution.emit(false);
                setTimeout(() => this.institution.emit(institutions[0]), 0);
              }
              return null;
            }),
            catchError((error: unknown) => {
              const apiError = httpErrorToApiError(error as HttpErrorResponse);
              if (apiError) {
                if ((apiError.code as ApiErrorCode) === ApiErrorCode.userMustProvideInstitution) {
                  this.userMustProvideInstitution.emit(true);
                  this.institution.emit();
                  setTimeout(() => this.formService.showFormExplainInfo(this.elementRef, apiError.message, apiError.traceId));
                } else {
                  this.errorService.handleHttpError(error as HttpErrorResponse);
                }
              } else {
                throwError(() => error);
              }
              return of(null);
            })
          )
        )
      )
    );
  }

  writeValue(value: string): void {
    this.value = value;
    this.valueSubject.next({ val: value, isInitial: true });
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  private onChange: (value: string) => void;

  private onTouched = (): void => {
    this.touched = true;
  };
}
